import React, { useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

export const Rain = forwardRef(function Rain({ count = 1000, isRaining: initialIsRaining = true }, ref) {
  const meshRef = useRef()
  const velocitiesRef = useRef([])
  const [isRaining, setIsRaining] = React.useState(initialIsRaining)
  
  // Expose control methods to parent
  useImperativeHandle(ref, () => ({
    setIsRaining,
    isRaining
  }))
  
  // Generate random positions and velocities
  const { matrices, velocities } = useMemo(() => {
    const matrices = []
    const velocities = []
    
    const area = 40 // Rain area size
    const height = 20 // Rain spawn height
    
    for (let i = 0; i < count; i++) {
      // Random position
      const x = (Math.random() - 0.5) * area
      const y = Math.random() * height + 10 // Spawn above scene
      const z = (Math.random() - 0.5) * area
      
      // Random velocity (falling speed)
      const velocity = {
        x: 0, // No horizontal drift
        y: -(Math.random() * 0.12 + 0.08), // Falling speed (reduced by 60%)
        z: 0  // No horizontal drift
      }
      
      // Create transformation matrix
      const matrix = new THREE.Matrix4()
      matrix.compose(
        new THREE.Vector3(x, y, z),
        new THREE.Quaternion(),
        new THREE.Vector3(0.02, 0.4, 0.02) // Thin cylinder scale
      )
      
      matrices.push(matrix)
      velocities.push(velocity)
    }
    
    velocitiesRef.current = velocities
    return { matrices, velocities }
  }, [count])
  
  // Set initial matrices
  useEffect(() => {
    if (meshRef.current) {
      matrices.forEach((matrix, i) => {
        meshRef.current.setMatrixAt(i, matrix)
      })
      meshRef.current.instanceMatrix.needsUpdate = true
    }
  }, [matrices])
  
  // Animation loop
  useFrame(() => {
    if (!meshRef.current || !isRaining) return
    
    const tempMatrix = new THREE.Matrix4()
    const position = new THREE.Vector3()
    const quaternion = new THREE.Quaternion()
    const scale = new THREE.Vector3(0.02, 0.4, 0.02) // Fixed scale for raindrops
    
    for (let i = 0; i < count; i++) {
      // Get current matrix
      meshRef.current.getMatrixAt(i, tempMatrix)
      tempMatrix.decompose(position, quaternion, new THREE.Vector3()) // Discard extracted scale
      
      // Update position with velocity
      const velocity = velocitiesRef.current[i]
      position.x += velocity.x
      position.y += velocity.y
      position.z += velocity.z
      
      // Reset when raindrop hits ground
      if (position.y < 0) {
        position.y = Math.random() * 10 + 15 // Respawn at top
        position.x = (Math.random() - 0.5) * 40
        position.z = (Math.random() - 0.5) * 40
      }
      
      // Update matrix with fixed scale
      tempMatrix.compose(position, quaternion, scale)
      meshRef.current.setMatrixAt(i, tempMatrix)
    }
    
    meshRef.current.instanceMatrix.needsUpdate = true
  })
  
  if (!isRaining) return null
  
  return (
    <instancedMesh ref={meshRef} args={[null, null, count]}>
      {/* Thin cylinder geometry for raindrops */}
      <cylinderGeometry args={[0.02, 0.02, 1, 4]} />
      
      {/* Semi-transparent blue material */}
      <meshBasicMaterial
        color="rgba(169, 169, 197, 1)"
        transparent
        opacity={0.4}
      />
    </instancedMesh>
  )
})